import { IcoMaximize16, IcoMinimize16 } from '@onefootprint/icons';
import { AnimatedChevronIcon, Divider } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import * as d3 from 'd3';
import { useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
export type NodeType = 'subject' | 'person' | 'business' | 'account' | 'transaction' | 'document';
export type EdgeType = 'owns' | 'controls' | 'transacts' | 'linked_to' | 'documents' | 'unknown';
export type GraphNode = {
  id: string;
  label: string;
  sublabel?: string;
  type: NodeType;
};
export type GraphEdge = {
  from: string;
  to: string;
  type: EdgeType;
  label?: string;
};
type NetworkGraphProps = {
  title?: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  height?: number | string;
};
const NODE: Record<NodeType, {
  fill: string;
  stroke: string;
  r: number;
  label: string;
  initial: string;
}> = {
  subject: {
    fill: '#eff6ff',
    stroke: '#2563eb',
    r: 20,
    label: 'Subject',
    initial: '◎'
  },
  person: {
    fill: '#eef2ff',
    stroke: '#4f46e5',
    r: 14,
    label: 'Person',
    initial: '?'
  },
  business: {
    fill: '#f0fdf4',
    stroke: '#059669',
    r: 15,
    label: 'Business',
    initial: 'B'
  },
  account: {
    fill: '#f8fafc',
    stroke: '#64748b',
    r: 13,
    label: 'Account',
    initial: '$'
  },
  transaction: {
    fill: '#fffbeb',
    stroke: '#d97706',
    r: 12,
    label: 'Transaction',
    initial: '→'
  },
  document: {
    fill: '#faf5ff',
    stroke: '#7c3aed',
    r: 12,
    label: 'Document',
    initial: '□'
  }
} as const;
const EDGE: Record<EdgeType, {
  color: string;
  active: string;
  dash: string | null;
  w: number;
  label: string;
}> = {
  owns: {
    color: '#cbd5e1',
    active: '#2563eb',
    dash: null,
    w: 1.5,
    label: 'Owns'
  },
  controls: {
    color: '#fde68a',
    active: '#d97706',
    dash: null,
    w: 2,
    label: 'Controls'
  },
  transacts: {
    color: '#bbf7d0',
    active: '#059669',
    dash: null,
    w: 2,
    label: 'Transacts'
  },
  linked_to: {
    color: '#bfdbfe',
    active: '#4f46e5',
    dash: null,
    w: 1.5,
    label: 'Linked to'
  },
  documents: {
    color: '#e9d5ff',
    active: '#7c3aed',
    dash: '4,3',
    w: 1.5,
    label: 'Documents'
  },
  unknown: {
    color: '#e2e8f0',
    active: '#94a3b8',
    dash: '5,4',
    w: 1.5,
    label: 'Unknown'
  }
} as const;
type SimNode = d3.SimulationNodeDatum & GraphNode;
type SimEdge = d3.SimulationLinkDatum<SimNode> & {
  edgeType: EdgeType;
  edgeLabel?: string;
};
type Tooltip = {
  x: number;
  y: number;
  node: GraphNode;
} | null;

// Data from the backend/AI isn't guaranteed to match our closed unions — unknown
// values would blow up `EDGE[type]`/`NODE[type]` lookups in d3 callbacks. Normalize
// at the boundary so every downstream access is safe.
const toEdgeType = (t: string): EdgeType => t as EdgeType in EDGE ? t as EdgeType : 'unknown';
const toNodeType = (t: string): NodeType => t as NodeType in NODE ? t as NodeType : 'person';
const getInitial = (node: GraphNode): string => {
  if (node.type === 'subject' || node.type === 'account' || node.type === 'transaction' || node.type === 'document') {
    return NODE[node.type].initial;
  }
  const words = node.label.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return node.label.substring(0, 2).toUpperCase();
};
type FitToViewArgs = {
  svgEl: SVGSVGElement;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>;
  padding: number;
  maxScale: number;
};
const fitToView = ({
  svgEl,
  g,
  zoom,
  padding,
  maxScale
}: FitToViewArgs) => {
  const gNode = g.node();
  if (!gNode) return;
  const bbox = gNode.getBBox();
  if (bbox.width < 1 || bbox.height < 1) return;
  const W = svgEl.clientWidth;
  const H = svgEl.clientHeight;
  const scale = Math.min((W - padding * 2) / bbox.width, (H - padding * 2) / bbox.height, maxScale);
  const tx = W / 2 - (bbox.x + bbox.width / 2) * scale;
  const ty = H / 2 - (bbox.y + bbox.height / 2) * scale;
  d3.select(svgEl).transition().duration(400).call(zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
};
const NetworkGraph = ({
  title,
  nodes = [],
  edges = [],
  height
}: NetworkGraphProps) => {
  const computedHeight = height ?? Math.max(600, Math.min(1000, nodes.length * 100));
  const isMobile = useMobileLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const gRef = useRef<d3.Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const [tooltip, setTooltip] = useState<Tooltip>(null);
  // An open legend covers much of a phone-width canvas, so it starts collapsed there. Derived rather
  // than seeded because isMobile is false on the first render.
  const [legendOpen, setLegendOpen] = useState<boolean | null>(null);
  const isLegendOpen = legendOpen ?? !isMobile;
  const [expanded, setExpanded] = useState(false);
  const expandedPrev = useRef(false);
  useEffect(() => {
    if (!svgRef.current || !containerRef.current || !nodes.length) return;
    const el = svgRef.current;
    const svg = d3.select(el);
    svg.selectAll('*').remove();
    const W = containerRef.current.clientWidth || 900;
    const H = containerRef.current.clientHeight || (typeof computedHeight === 'number' ? computedHeight : 600);
    const defs = svg.append('defs');
    const dotPattern = defs.append('pattern').attr('id', 'ng-dots').attr('width', 16).attr('height', 16).attr('patternUnits', 'userSpaceOnUse');
    dotPattern.append('rect').attr('width', 16).attr('height', 16).attr('fill', 'var(--background-color-secondary)').attr('opacity', 0.5);
    dotPattern.append('circle').attr('cx', 8).attr('cy', 8).attr('r', 0.8).attr('fill', 'var(--border-color-primary)');
    svg.insert('rect', ':first-child').attr('width', '100%').attr('height', '100%').attr('fill', 'url(#ng-dots)');
    const shadow = defs.append('filter').attr('id', 'ng-shadow').attr('x', '-30%').attr('y', '-30%').attr('width', '160%').attr('height', '160%');
    shadow.append('feDropShadow').attr('dx', 0).attr('dy', 1.5).attr('stdDeviation', 2.5).attr('flood-color', 'rgba(0,0,0,0.09)');
    (Object.keys(EDGE) as EdgeType[]).forEach(t => {
      defs.append('marker').attr('id', `ng-arrow-${t}`).attr('viewBox', '0 -4 9 8').attr('refX', 30).attr('refY', 0).attr('markerWidth', 5).attr('markerHeight', 5).attr('orient', 'auto').append('path').attr('d', 'M0,-4L9,0L0,4Z').attr('fill', EDGE[t].color);
    });
    const g = svg.append('g');
    gRef.current = g;
    const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.15, 5]).on('zoom', e => g.attr('transform', e.transform));
    zoomRef.current = zoom;
    svg.call(zoom);
    const simNodes: SimNode[] = nodes.map(n => ({
      ...n,
      type: toNodeType(n.type)
    }));
    const byId = new Map(simNodes.map(n_0 => [n_0.id, n_0]));
    const simEdges: SimEdge[] = edges.filter(e_0 => byId.has(e_0.from) && byId.has(e_0.to)).map(e_1 => ({
      source: byId.get(e_1.from)!,
      target: byId.get(e_1.to)!,
      edgeType: toEdgeType(e_1.type),
      edgeLabel: e_1.label
    }));
    const linkG = g.append('g');
    const linkSel = linkG.selectAll<SVGLineElement, SimEdge>('line').data(simEdges).join('line').attr('stroke', d => EDGE[d.edgeType].color).attr('stroke-width', d_0 => EDGE[d_0.edgeType].w).attr('stroke-dasharray', d_1 => EDGE[d_1.edgeType].dash).attr('marker-end', d_2 => `url(#ng-arrow-${d_2.edgeType})`);
    const elG = g.append('g');
    const elData = simEdges.filter(e_2 => e_2.edgeLabel && e_2.edgeLabel.trim().length > 0);
    elG.selectAll<SVGRectElement, SimEdge>('rect').data(elData).join('rect').attr('rx', 3).attr('height', 14).attr('fill', 'var(--background-color-primary)').attr('stroke', 'var(--border-color-tertiary)').attr('stroke-width', 1);
    const elTx = elG.selectAll<SVGTextElement, SimEdge>('text').data(elData).join('text').attr('font-size', 8).attr('font-family', 'inherit').attr('fill', 'var(--text-color-tertiary)').attr('text-anchor', 'middle').attr('dominant-baseline', 'central').text(d_3 => d_3.edgeLabel ?? '');
    const nodeG = g.append('g');
    const nodeSel = nodeG.selectAll<SVGGElement, SimNode>('g').data(simNodes).join('g').attr('cursor', 'pointer').on('mouseenter', (event, d_4) => {
      const rect = el.getBoundingClientRect();
      setTooltip({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        node: d_4
      });
      const connected = new Set<string>([d_4.id]);
      simEdges.forEach(e_3 => {
        const s = (e_3.source as SimNode).id,
          t_0 = (e_3.target as SimNode).id;
        if (s === d_4.id) connected.add(t_0);
        if (t_0 === d_4.id) connected.add(s);
      });
      nodeSel.attr('opacity', n_1 => connected.has(n_1.id) ? 1 : 0.2);
      linkSel.attr('stroke', e_4 => {
        const s_0 = (e_4.source as SimNode).id,
          t_1 = (e_4.target as SimNode).id;
        return s_0 === d_4.id || t_1 === d_4.id ? EDGE[e_4.edgeType].active : EDGE[e_4.edgeType].color;
      }).attr('stroke-opacity', e_5 => {
        const s_1 = (e_5.source as SimNode).id,
          t_2 = (e_5.target as SimNode).id;
        return s_1 === d_4.id || t_2 === d_4.id ? 1 : 0.15;
      }).attr('stroke-width', e_6 => {
        const s_2 = (e_6.source as SimNode).id,
          t_3 = (e_6.target as SimNode).id;
        return s_2 === d_4.id || t_3 === d_4.id ? EDGE[e_6.edgeType].w + 0.5 : EDGE[e_6.edgeType].w;
      });
    }).on('mouseleave', () => {
      setTooltip(null);
      nodeSel.attr('opacity', 1);
      linkSel.attr('stroke', d_5 => EDGE[d_5.edgeType].color).attr('stroke-opacity', 1).attr('stroke-width', d_6 => EDGE[d_6.edgeType].w);
    }).call(d3.drag<SVGGElement, SimNode>().on('start', (ev, d_7) => {
      if (!ev.active) sim.alphaTarget(0.3).restart();
      d_7.fx = d_7.x;
      d_7.fy = d_7.y;
    }).on('drag', (ev_0, d_8) => {
      d_8.fx = ev_0.x;
      d_8.fy = ev_0.y;
    }).on('end', (ev_1, d_9) => {
      if (!ev_1.active) sim.alphaTarget(0);
      d_9.fx = null;
      d_9.fy = null;
    }));
    nodeSel.append('circle').attr('r', d_10 => NODE[d_10.type].r).attr('fill', d_11 => NODE[d_11.type].fill).attr('stroke', d_12 => NODE[d_12.type].stroke).attr('stroke-width', d_13 => d_13.type === 'subject' ? 2.5 : 1.5).attr('filter', 'url(#ng-shadow)');
    nodeSel.filter(d_14 => d_14.type === 'subject').append('circle').attr('r', d_15 => NODE[d_15.type].r + 4).attr('fill', 'none').attr('stroke', NODE.subject.stroke).attr('stroke-width', 1).attr('stroke-dasharray', '3,3').attr('stroke-opacity', 0.4);
    nodeSel.append('text').attr('text-anchor', 'middle').attr('dominant-baseline', 'central').attr('font-size', d_16 => d_16.type === 'subject' ? 9 : 8).attr('font-weight', '600').attr('font-family', 'ui-monospace, monospace').attr('fill', d_17 => NODE[d_17.type].stroke).attr('pointer-events', 'none').text(d_18 => getInitial(d_18));
    nodeSel.append('text').attr('text-anchor', 'middle').attr('y', d_19 => NODE[d_19.type].r + 12).attr('font-size', d_20 => d_20.type === 'subject' ? 10 : 9).attr('font-weight', d_21 => d_21.type === 'subject' ? '600' : '500').attr('font-family', 'inherit').attr('fill', 'var(--text-color-primary)').text(d_22 => d_22.label);
    nodeSel.filter(d_23 => !!d_23.sublabel).append('text').attr('text-anchor', 'middle').attr('y', d_24 => NODE[d_24.type].r + 23).attr('font-size', 8).attr('font-family', 'inherit').attr('fill', 'var(--text-color-tertiary)').text(d_25 => d_25.sublabel ?? '');
    const sim = d3.forceSimulation(simNodes).force('link', d3.forceLink<SimNode, SimEdge>(simEdges).id(d_26 => d_26.id).distance(300).strength(0.3)).force('charge', d3.forceManyBody().strength(-2000)).force('center', d3.forceCenter(W / 2, H / 2).strength(0.05)).force('collide', d3.forceCollide<SimNode>(d_27 => NODE[d_27.type].r + 80)).on('tick', () => {
      linkSel.attr('x1', d_28 => (d_28.source as SimNode).x ?? 0).attr('y1', d_29 => (d_29.source as SimNode).y ?? 0).attr('x2', d_30 => (d_30.target as SimNode).x ?? 0).attr('y2', d_31 => (d_31.target as SimNode).y ?? 0);
      elTx.each(function (d_32, i) {
        const sx = (d_32.source as SimNode).x ?? 0,
          sy = (d_32.source as SimNode).y ?? 0;
        const tx = (d_32.target as SimNode).x ?? 0,
          ty = (d_32.target as SimNode).y ?? 0;
        const dx = tx - sx,
          dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        // Place label at 30% along edge (away from midpoint/nodes)
        const t_4 = 0.3;
        const px = sx + dx * t_4,
          py = sy + dy * t_4;
        // Push perpendicular to the edge, alternating sides
        const offsetDist = 16;
        const sign = i % 2 === 0 ? 1 : -1;
        const ox = -dy / len * offsetDist * sign,
          oy = dx / len * offsetDist * sign;
        const lx = px + ox,
          ly = py + oy;
        d3.select(this).attr('x', lx).attr('y', ly);
        try {
          const b = (this as SVGTextElement).getBBox();
          const rect_0 = d3.select(this.previousSibling as Element);
          if (b.width < 1) {
            rect_0.attr('display', 'none');
            d3.select(this).attr('display', 'none');
          } else {
            rect_0.attr('x', lx - b.width / 2 - 3).attr('y', ly - 7).attr('width', b.width + 6);
          }
        } catch (_) {}
      });
      nodeSel.attr('transform', d_33 => `translate(${d_33.x ?? 0},${d_33.y ?? 0})`);
    });

    // The forces spread even a few nodes wider than a phone-width canvas, so the settled layout is
    // fitted once. Only the first settle: a drag reheats the simulation and would undo the user's zoom.
    if (isMobile) {
      sim.on('end.fit', () => {
        sim.on('end.fit', null);
        if (!expandedPrev.current) fitToView({
          svgEl: el,
          g,
          zoom,
          padding: 24,
          maxScale: 1
        });
      });
    }
    return () => {
      sim.stop();
      // Tear down the zoom listeners explicitly — without this the next effect
      // run stacks duplicate zoom handlers on the same SVG element.
      svg.on('.zoom', null);
    };
  }, [nodes, edges, isMobile]);

  // d3-zoom claims every touch it accepts and cancels the page scroll, so on a phone the inline graph
  // only takes two-finger gestures and a one-finger swipe scrolls past it. Expanded, it owns the screen.
  useEffect(() => {
    zoomRef.current?.filter(event_0 => isMobile && !expanded && event_0.type === 'touchstart' ? event_0.touches.length > 1 : (!event_0.ctrlKey || event_0.type === 'wheel') && !event_0.button);
  }, [nodes, edges, isMobile, expanded]);
  useEffect(() => {
    if (expanded === expandedPrev.current) return;
    expandedPrev.current = expanded;
    if (!svgRef.current || !gRef.current || !zoomRef.current) return;
    const g_0 = gRef.current;
    const zoom_0 = zoomRef.current;

    // Wait a frame for the container to resize after expand/collapse.
    const rafId = requestAnimationFrame(() => {
      if (!svgRef.current) return;
      fitToView({
        svgEl: svgRef.current,
        g: g_0,
        zoom: zoom_0,
        padding: expanded ? 80 : 40,
        maxScale: expanded ? 1.2 : 1
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [expanded, computedHeight]);
  return <div className="flex flex-col gap-3 w-full">
      {title && <span className="text-label-2 text-primary fp-text">{title}</span>}
      <div ref={containerRef} className={expanded ? 'fixed inset-0 z-50 bg-primary' : 'relative w-full max-h-[420px] overflow-hidden rounded-default border border-solid border-tertiary md:max-h-none'} style={expanded ? undefined : {
      height: computedHeight
    }}>
        <svg ref={svgRef} role="img" aria-label={`Network graph with ${nodes.length} nodes and ${edges.length} edges`} width="100%" height="100%" style={{
        display: 'block'
      }} />

        <button type="button" onClick={() => setExpanded(e_7 => !e_7)} className={cx('absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-default border border-solid border-tertiary bg-primary shadow-sm cursor-pointer hover:bg-hover-control transition-colors', expanded && 'max-md:top-[max(0.75rem,env(safe-area-inset-top))]')}>
          {expanded ? <IcoMinimize16 color="tertiary" /> : <IcoMaximize16 color="tertiary" />}
        </button>

        <div className={cx('absolute top-3 left-3 overflow-hidden rounded-default border border-solid border-tertiary bg-primary shadow-sm', expanded && 'max-md:top-[max(0.75rem,env(safe-area-inset-top))]')} style={{
        minWidth: 120,
        maxWidth: 140
      }}>
          <button type="button" onClick={() => setLegendOpen(!isLegendOpen)} className="flex w-full cursor-pointer items-center justify-between bg-transparent px-3 py-2 text-left" style={{
          border: 'none'
        }}>
            <span className="text-label-2 text-tertiary fp-text">Legend</span>
            <AnimatedChevronIcon isOpen={isLegendOpen} className="text-tertiary" />
          </button>

          {isLegendOpen && <>
              <Divider />
              <div className="flex flex-col gap-3 px-3 py-2.5">
                <div className="flex flex-col gap-1.5">
                  {(Object.keys(NODE) as NodeType[]).filter(t_5 => nodes.some(n_2 => n_2.type === t_5)).map(t_6 => <div key={t_6} className="flex items-center gap-2">
                        <svg width={10} height={10} aria-hidden="true" style={{
                  flexShrink: 0
                }}>
                          <circle cx={5} cy={5} r={4} fill={NODE[t_6].fill} stroke={NODE[t_6].stroke} strokeWidth={1.5} />
                        </svg>
                        <span className="text-caption-1 text-secondary fp-text">{NODE[t_6].label}</span>
                      </div>)}
                </div>
                <Divider variant="secondary" />
                <div className="flex flex-col gap-1.5">
                  {(Object.keys(EDGE) as EdgeType[]).filter(t_7 => edges.some(e_8 => e_8.type === t_7)).map(t_8 => <div key={t_8} className="flex items-center gap-2">
                        <svg width={18} height={6} aria-hidden="true" style={{
                  flexShrink: 0
                }}>
                          <line x1={0} y1={3} x2={18} y2={3} stroke={EDGE[t_8].active} strokeWidth={1.5} strokeDasharray={EDGE[t_8].dash ?? undefined} />
                        </svg>
                        <span className="text-caption-1 text-secondary fp-text">{EDGE[t_8].label}</span>
                      </div>)}
                </div>
              </div>
            </>}
        </div>

        <div className={cx('absolute bottom-3 left-3', expanded && 'max-md:bottom-[max(0.75rem,env(safe-area-inset-bottom))]')}>
          <span className="text-caption-1 text-tertiary">
            {nodes.length} nodes · {edges.length} edges
            <span className="hidden md:inline"> · scroll to zoom</span>
            {!expanded && <span className="md:hidden"> · two fingers to pan</span>}
          </span>
        </div>

        {tooltip && <div className="pointer-events-none absolute z-50 rounded-md border border-tertiary bg-primary px-2 py-1.5 shadow-md" style={{
        left: tooltip.x + 16,
        top: tooltip.y - 12
      }}>
            <div className="flex items-center gap-1 mb-2">
              <svg width={8} height={8} aria-hidden="true">
                <circle cx={4} cy={4} r={3} fill={NODE[tooltip.node.type].fill} stroke={NODE[tooltip.node.type].stroke} strokeWidth={1.5} />
              </svg>
              <span className="text-caption-1 text-tertiary">{NODE[tooltip.node.type].label}</span>
            </div>
            <p className="text-caption-1 font-medium text-primary">{tooltip.node.label}</p>
            {tooltip.node.sublabel && <p className="text-caption-1 text-tertiary mt-[2px]">{tooltip.node.sublabel}</p>}
          </div>}
      </div>
    </div>;
};
export default NetworkGraph;